import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { invoiceApi, invoiceItemDtlApi, productApi, productPriceApi, productCategoryApi, taxConfigApi, inventoryItemDtlApi } from "../api/api";
import { emitDataMutation } from "../utils/appEvents";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";
import PrintInvoiceModal from "../components/PrintInvoiceModal";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";

/* ── Status config ── */
const STATUS_CONFIG = {
  Draft: { color: "draft", label: "Draft" },
  Finalised: { color: "finalised", label: "Finalised" },
  Cancelled: { color: "cancelled", label: "Cancelled" },
};

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ title, message, confirmLabel, confirmClass, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop" style={{ zIndex: 500 }}>
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><AlertIcon /></div>
        <h3 className="rv-dialog-title">{title}</h3>
        <p className="rv-dialog-desc">{message}</p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Go Back</button>
          <button className={`rv-btn ${confirmClass}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Invoice Form Modal ── */
/* ── Invoice Form Modal ── */
function InvoiceModal({ initial, products, allItems, inventoryItems = [], prices, categories, taxConfigs, onSave, onClose, saving }) {
  const isEdit = !!initial?.invoiceID;
  const isLocked = initial?.invoiceStatus === "Finalised" || initial?.invoiceStatus === "Cancelled";
  const todayISO = new Date().toISOString().split("T")[0];

  /* ── Lookup maps (built once) ── */
  // productID → latest price record
  const latestPriceMap = useMemo(() => {
    const map = new Map();
    prices.forEach(p => {
      const existing = map.get(p.productID);
      if (!existing || new Date(p.effdt) > new Date(existing.effdt)) {
        map.set(p.productID, p);
      }
    });
    return map;
  }, [prices]);

  // categoryID → taxConfigID
  const categoryTaxMap = useMemo(() => {
    const map = new Map();
    categories.forEach(c => {
      if (c.taxConfigID) map.set(c.categoryID, c.taxConfigID);
    });
    return map;
  }, [categories]);

  // Add this alongside the other useMemo maps
  const categoryMap = useMemo(() =>
    new Map(categories.map(c => [Number(c.categoryID), c])),
    [categories]);

  // inventoryItemID → inventoryItem object
  const inventoryItemMap = useMemo(() =>
    new Map((inventoryItems || []).map(i => [Number(i.inventoryItemID), i])),
    [inventoryItems]);

  // taxConfigID → latest taxPercentage
  const taxRateMap = useMemo(() => {
    const map = new Map();
    taxConfigs.forEach(t => {
      const existing = map.get(t.taxConfigID);
      if (!existing || new Date(t.effdt) > new Date(existing.effdt)) {
        map.set(t.taxConfigID, t);
      }
    });
    return map;
  }, [taxConfigs]);

  // productID → product object
  const productMap = useMemo(() =>
    new Map(products.map(p => [Number(p.productID), p])),
    [products]);

  /* ── Helper: resolve auto-fill values for a productID ── */
  const resolveProductDefaults = useCallback((productID) => {
    const pid = Number(productID);
    const product = productMap.get(pid);
    if (!product) return { unitPrice: "", taxConfigID: 0, taxPercentage: "" };

    const price = latestPriceMap.get(pid);
    const taxConfigID = categoryTaxMap.get(Number(product.categoryID)) ?? 0;
    const taxConfig = taxConfigID ? taxRateMap.get(taxConfigID) : null;

    return {
      unitPrice: price?.grossSellingPrice != null ? String(price.grossSellingPrice) : "",
      taxConfigID: taxConfigID,
      taxPercentage: taxConfig?.taxPercentage != null ? String(taxConfig.taxPercentage) : "",
    };
  }, [productMap, latestPriceMap, categoryTaxMap, taxRateMap]);

  /* ── Form state ── */
  const [form, setForm] = useState({
    invoiceNumber: initial?.invoiceNumber ?? "",
    invoiceDate: initial?.invoiceDate ? initial.invoiceDate.split("T")[0] : todayISO,
    customerName: initial?.customerName ?? "",
    customerPhone: initial?.customerPhone ?? "",
    customerGSTIN: initial?.customerGSTIN ?? "",
  });

  const blankLine = () => ({
    invoiceItemID: 0,
    productID: "",
    inventoryItemID: "",
    quantity: "",
    billedWeight: "",
    unitPrice: "",
    taxConfigID: 0,
    taxPercentage: "",
    lineAmount: 0,
    taxAmount: 0,
    totalPrice: 0,
  });

  const [lines, setLines] = useState(
    initial?.lines?.length
      ? initial.lines.map(l => ({ ...blankLine(), ...l }))
      : [blankLine()]
  );

  const [itemMeta, setItemMeta] = useState({});
  const [errors, setErrors] = useState({});
  const [lineErrors, setLineErrors] = useState([]);

  // Auto-resolve existing lines with inventoryItemID on modal load
  useEffect(() => {
    lines.forEach((line, idx) => {
      if (line.inventoryItemID) {
        const id = Number(line.inventoryItemID);
        if (id > 0) {
          if (inventoryItemMap.has(id)) {
            setItemMeta(prev => ({ ...prev, [idx]: inventoryItemMap.get(id) }));
          } else {
            inventoryItemDtlApi.getById(id)
              .then(res => {
                const item = res?.data ?? res;
                if (item && item.inventoryItemID) {
                  setItemMeta(prev => ({ ...prev, [idx]: item }));
                }
              })
              .catch(() => {});
          }
        }
      }
    });
  }, [inventoryItemMap]);

  /* ── Auto-calc amounts for one line ── */
  const calcLine = useCallback((line) => {
    const qty = Number(line.quantity) || 0;
    const unitPrice = Number(line.unitPrice) || 0;
    const taxPct = Number(line.taxPercentage) || 0;
    const lineAmount = Math.round(qty * unitPrice * 100) / 100;
    const taxAmount = Math.round(lineAmount * taxPct) / 100;
    const totalPrice = Math.round((lineAmount + taxAmount) * 100) / 100;
    return { ...line, lineAmount, taxAmount, totalPrice };
  }, []);

  /* ── Handle inventory item change — auto-fill billed weight & product ── */
  const handleInventoryItemChange = (idx, rawVal) => {
    // Clear meta immediately
    if (!rawVal || rawVal === "0" || rawVal === "") {
      setLines(prev => {
        const next = [...prev];
        next[idx] = calcLine({ ...next[idx], inventoryItemID: rawVal, billedWeight: "" });
        return next;
      });
      setItemMeta(prev => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
      return;
    }

    const id = Number(rawVal);
    if (!id || id <= 0) return;

    // Set the ID immediately so the input is responsive
    setLines(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], inventoryItemID: rawVal };
      return next;
    });

    if (inventoryItemMap.has(id)) {
      const item = inventoryItemMap.get(id);
      setItemMeta(prev => ({ ...prev, [idx]: item }));
      setLines(prev => {
        const next = [...prev];
        const curr = next[idx];
        if (!curr) return prev;
        const billedWeight = item.netWeight != null
          ? String(item.netWeight)
          : item.grossWeight != null ? String(item.grossWeight) : "";
        let updated = { ...curr, billedWeight, inventoryItemID: rawVal };
        if (!curr.productID && item.productID) {
          updated = { ...updated, productID: String(item.productID), ...resolveProductDefaults(item.productID) };
        }
        next[idx] = calcLine(updated);
        return next;
      });
    } else {
      setItemMeta(prev => ({ ...prev, [idx]: { loading: true } }));
      inventoryItemDtlApi.getById(id)
        .then(res => {
          const item = res?.data ?? res;
          if (item && (item.inventoryItemID || item.productID != null)) {
            setItemMeta(prev => ({ ...prev, [idx]: item }));
            setLines(prev => {
              const next = [...prev];
              const curr = next[idx];
              if (!curr) return prev;
              const billedWeight = item.netWeight != null
                ? String(item.netWeight)
                : item.grossWeight != null ? String(item.grossWeight) : "";
              let updated = { ...curr, billedWeight, inventoryItemID: rawVal };
              if (!curr.productID && item.productID) {
                updated = { ...updated, productID: String(item.productID), ...resolveProductDefaults(item.productID) };
              }
              next[idx] = calcLine(updated);
              return next;
            });
          } else {
            setItemMeta(prev => ({ ...prev, [idx]: { error: `Item #${id} not found` } }));
          }
        })
        .catch(() => {
          setItemMeta(prev => ({ ...prev, [idx]: { error: `Item #${id} not found` } }));
        });
    }
  };

  /* ── Handle product selection — auto-fill unit price + tax ── */
  const handleProductChange = (idx, productID) => {
    setLines(prev => {
      const next = [...prev];
      const defaults = resolveProductDefaults(productID);
      next[idx] = calcLine({
        ...next[idx],
        productID,
        ...defaults,
        // reset quantity and weight — they are always per-item unless already set by inventory item
        quantity: next[idx].quantity || "",
        billedWeight: next[idx].billedWeight || "",
      });
      return next;
    });
    setLineErrors(prev => {
      const next = [...prev];
      if (next[idx]) next[idx] = { ...next[idx], productID: "" };
      return next;
    });
  };

  /* ── Generic line field update ── */
  const updateLine = (idx, key, val) => {
    setLines(prev => {
      const next = [...prev];
      next[idx] = calcLine({ ...next[idx], [key]: val });
      return next;
    });
    setLineErrors(prev => {
      const next = [...prev];
      if (next[idx]) next[idx] = { ...next[idx], [key]: "" };
      return next;
    });
  };

  const addLine = () => setLines(prev => [...prev, blankLine()]);
  const removeLine = (idx) => {
    setLines(prev => prev.filter((_, i) => i !== idx));
    setItemMeta(prev => {
      const next = {};
      Object.keys(prev).forEach(k => {
        const ki = Number(k);
        if (ki < idx) next[ki] = prev[ki];
        else if (ki > idx) next[ki - 1] = prev[ki];
      });
      return next;
    });
  };

  /* ── Barcode Scanner State & Logic ── */
  const [scanCode, setScanCode] = useState("");
  const [scanLoading, setScanLoading] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const barcodeInputRef = useRef(null);

  const modalScanBuffer = useRef("");
  const modalLastKeyTime = useRef(0);
  const modalScanTimer = useRef(null);

  // Audio beep on scan (A5 880Hz for success, 320Hz for error/warn)
  const playBeep = (success = true) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = success ? 880 : 320;
      gain.gain.value = 0.12;
      osc.start();
      osc.stop(ctx.currentTime + (success ? 0.1 : 0.2));
    } catch (_) {}
  };

  const handleBarcodeScan = useCallback(async (rawCode) => {
    const code = String(rawCode || "").trim();
    if (!code) return;
    const num = parseInt(code, 10);
    if (!num || isNaN(num)) {
      setScanStatus({ type: "error", message: `Invalid barcode tag: "${code}"` });
      playBeep(false);
      return;
    }

    setScanLoading(true);
    setScanStatus(null);

    try {
      // 1. Look up in inventoryItemMap or array (checking inventoryItemID & barcodeID)
      let item = inventoryItemMap.get(num) ||
        (inventoryItems || []).find(i => Number(i.inventoryItemID) === num || Number(i.barcodeID) === num);

      // 2. Fallback to API if not present in memory
      if (!item) {
        try {
          const res = await inventoryItemDtlApi.getById(num);
          item = res?.data ?? res;
        } catch (_) {
          const allRes = await inventoryItemDtlApi.getAll().catch(() => []);
          const allList = Array.isArray(allRes) ? allRes : (allRes?.data ?? []);
          item = allList.find(i => Number(i.inventoryItemID) === num || Number(i.barcodeID) === num) || null;
        }
      }

      if (!item || (!item.inventoryItemID && item.productID == null)) {
        setScanStatus({ type: "error", message: `Tag #${code} not found in inventory.` });
        playBeep(false);
        setScanLoading(false);
        return;
      }

      const invItemId = Number(item.inventoryItemID);

      // 3. Check if already added to lines
      const existingIdx = lines.findIndex(l => Number(l.inventoryItemID) === invItemId);
      if (existingIdx !== -1) {
        setScanStatus({
          type: "warn",
          message: `Item #${invItemId} is already added in Line ${existingIdx + 1}.`,
        });
        playBeep(false);
        setScanCode("");
        setScanLoading(false);
        return;
      }

      // 4. Resolve product defaults
      const prodId = item.productID ? String(item.productID) : "";
      const defaults = prodId ? resolveProductDefaults(prodId) : {};
      const billedWeight = item.netWeight != null
        ? String(item.netWeight)
        : item.grossWeight != null ? String(item.grossWeight) : "";

      const product = prodId ? productMap.get(Number(prodId)) : null;

      const newLine = calcLine({
        invoiceItemID: 0,
        productID: prodId,
        inventoryItemID: String(invItemId),
        quantity: 1,
        billedWeight,
        ...defaults,
      });

      // 5. Append or replace blank initial line
      setLines(prev => {
        if (prev.length === 1 && !prev[0].productID && !prev[0].inventoryItemID && !prev[0].quantity) {
          setItemMeta({ 0: item });
          return [newLine];
        }
        const nextIdx = prev.length;
        setItemMeta(m => ({ ...m, [nextIdx]: item }));
        return [...prev, newLine];
      });

      // Clear lines validation error
      setErrors(prev => ({ ...prev, lines: "" }));

      playBeep(true);
      setScanStatus({
        type: "success",
        message: `✓ Added Item #${invItemId} (${product?.productName || "Product"} · ${billedWeight || 0}g)`,
      });
      setScanCode("");
      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 50);
    } catch (err) {
      setScanStatus({ type: "error", message: `Could not load tag #${code}: ${err.message || "Network error"}` });
      playBeep(false);
    } finally {
      setScanLoading(false);
    }
  }, [inventoryItemMap, inventoryItems, lines, productMap, resolveProductDefaults, calcLine]);

  /* Auto-focus barcode input when opening a new invoice */
  useEffect(() => {
    if (!isLocked && !isEdit) {
      const t = setTimeout(() => barcodeInputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
  }, [isLocked, isEdit]);

  /* Auto-resolve initial lines that have productID but empty price/tax */
  useEffect(() => {
    setLines(prev => {
      let changed = false;
      const updated = prev.map(line => {
        if (line.productID && (line.unitPrice === "" || line.unitPrice === undefined)) {
          changed = true;
          const defaults = resolveProductDefaults(line.productID);
          return calcLine({
            ...line,
            ...defaults,
          });
        }
        return line;
      });
      return changed ? updated : prev;
    });
  }, [resolveProductDefaults, calcLine]);

  /* Global keystroke listener inside modal for barcode gun wedge */
  useEffect(() => {
    if (isLocked) return;

    const onModalKeyDown = (e) => {
      const activeEl = document.activeElement;

      // If focused inside barcodeInputRef, input's own onKeyDown handles Enter
      if (activeEl === barcodeInputRef.current) return;

      // If user is typing in standard text inputs (Customer name, notes, etc.)
      if (activeEl && (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA" || activeEl.tagName === "SELECT")) {
        const now = Date.now();
        const gap = now - modalLastKeyTime.current;
        modalLastKeyTime.current = now;

        if (gap > 45 && modalScanBuffer.current.length > 0) {
          modalScanBuffer.current = "";
        }
        if (e.key === "Enter" && modalScanBuffer.current.length >= 1) {
          const scanned = modalScanBuffer.current.trim();
          modalScanBuffer.current = "";
          e.preventDefault();
          handleBarcodeScan(scanned);
          return;
        }
        if (e.key.length === 1 && gap <= 45) {
          modalScanBuffer.current += e.key;
        }
        return;
      }

      // If cashier clicked anywhere on modal surface:
      const now = Date.now();
      const gap = now - modalLastKeyTime.current;
      modalLastKeyTime.current = now;

      if (e.key === "Enter") {
        const val = modalScanBuffer.current.trim();
        modalScanBuffer.current = "";
        clearTimeout(modalScanTimer.current);
        if (val.length >= 1) {
          e.preventDefault();
          handleBarcodeScan(val);
        }
        return;
      }

      if (e.key.length !== 1) return;

      if (gap > 400) {
        modalScanBuffer.current = e.key;
      } else {
        modalScanBuffer.current += e.key;
      }

      clearTimeout(modalScanTimer.current);
      modalScanTimer.current = setTimeout(() => {
        const val = modalScanBuffer.current.trim();
        modalScanBuffer.current = "";
        if (val.length >= 2) {
          handleBarcodeScan(val);
        }
      }, 150);
    };

    window.addEventListener("keydown", onModalKeyDown);
    return () => {
      window.removeEventListener("keydown", onModalKeyDown);
      clearTimeout(modalScanTimer.current);
    };
  }, [isLocked, handleBarcodeScan]);

  /* ── Totals ── */
  const totalAmount = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const totalTaxAmount = lines.reduce((s, l) => s + (l.taxAmount || 0), 0);
  const grossAmount = lines.reduce((s, l) => s + (l.totalPrice || 0), 0);

  /* ── Validate ── */
  const validate = () => {
    const e = {};
    if (!form.invoiceNumber.trim()) e.invoiceNumber = "Invoice number is required.";
    if (!form.invoiceDate) e.invoiceDate = "Invoice date is required.";
    if (!form.customerName.trim()) e.customerName = "Customer name is required.";
    if (lines.length === 0) e.lines = "At least one line item is required.";

    const le = lines.map(l => {
      const err = {};
      if (!l.productID) err.productID = "Required.";
      if (!l.quantity || Number(l.quantity) <= 0) err.quantity = "Required.";
      if (l.unitPrice === "" || Number(l.unitPrice) < 0) err.unitPrice = "Required.";
      return err;
    });
    setLineErrors(le);
    const linesValid = le.every(e => Object.keys(e).length === 0);
    return linesValid && Object.keys(e).length === 0 ? null : e;
  };

  const handleSubmit = (status) => {
    const e = validate();
    if (e) { setErrors(e); return; }
    onSave({
      header: {
        ...form,
        invoiceStatus: status,
        totalAmount: parseFloat(totalAmount.toFixed(2)),
        totalTaxAmount: parseFloat(totalTaxAmount.toFixed(2)),
        grossAmount: parseFloat(grossAmount.toFixed(2)),
        ...(isEdit ? { invoiceID: initial.invoiceID } : {}),
      },
      lines: lines.map(l => ({
        invoiceItemID: Number(l.invoiceItemID) || 0,
        productID: Number(l.productID),
        inventoryItemID: l.inventoryItemID ? Number(l.inventoryItemID) : null,
        quantity: Number(l.quantity),
        billedWeight: parseFloat(l.billedWeight) || 0,
        unitPrice: parseFloat(l.unitPrice) || 0,
        taxConfigID: Number(l.taxConfigID) || 0,
        taxPercentage: parseFloat(l.taxPercentage) || 0,
        lineAmount: l.lineAmount,
        taxAmount: l.taxAmount,
        totalPrice: l.totalPrice,
      })),
    });
  };

  const fmtAmt = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;

  /* ── Price hint for a line ── */
  const getPriceHint = (productID) => {
    if (!productID) return null;
    const price = latestPriceMap.get(Number(productID));
    if (!price) return { text: "No price configured for this product", warn: true };
    const date = new Date(price.effdt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    return { text: `Price as of ${date} · Landing ₹${price.landingCost} · Margin ${price.marginPercent}%`, warn: false };
  };

  /* ── Tax hint for a line ── */
  const getTaxHint = (productID) => {
    if (!productID) return null;
    const product = productMap.get(Number(productID));
    if (!product) return null;
    const taxConfigID = categoryTaxMap.get(Number(product.categoryID));
    if (!taxConfigID) return { text: "No tax configured for this product's category", warn: true };
    const taxConfig = taxRateMap.get(taxConfigID);
    return taxConfig
      ? { text: `${taxConfig.taxName} · ${taxConfig.taxPercentage}%`, warn: false }
      : null;
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form" style={{ maxWidth: 760, width: "100%", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>

        <div className="rv-modal-header" style={{ flexShrink: 0, paddingRight: 48 }}>
          <div className="rv-modal-icon"><InvoiceIcon /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <h3 className="rv-modal-title" style={{ marginBottom: 0 }}>
                {isLocked
                  ? `Invoice ${initial.invoiceNumber}`
                  : isEdit ? "Edit Invoice" : "New Invoice"}
              </h3>
              {initial?.invoiceStatus && (
                <span className={`rv-inv-status-badge rv-inv-status--${STATUS_CONFIG[initial.invoiceStatus]?.color ?? "draft"}`}>
                  {initial.invoiceStatus}
                </span>
              )}
            </div>
            <p className="rv-modal-sub" style={{ marginTop: 3 }}>
              {isLocked
                ? `Status: ${initial.invoiceStatus} — read only`
                : isEdit ? "Update invoice details." : "Create a new customer invoice."}
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body" style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>

          {/* ── Header fields ── */}
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Invoice Number <span className="rv-required">*</span></label>
              <input
                className={`rv-input${errors.invoiceNumber ? " rv-input--err" : ""}`}
                placeholder="e.g. INV-2026-001"
                value={form.invoiceNumber}
                disabled={isLocked}
                onChange={e => { setForm(f => ({ ...f, invoiceNumber: e.target.value })); setErrors(v => ({ ...v, invoiceNumber: "" })); }}
              />
              {errors.invoiceNumber && <p className="rv-field-err">{errors.invoiceNumber}</p>}
            </div>
            <div className="rv-field">
              <label className="rv-label">Invoice Date <span className="rv-required">*</span></label>
              <input
                className={`rv-input rv-input--date${errors.invoiceDate ? " rv-input--err" : ""}`}
                type="date"
                value={form.invoiceDate}
                disabled={isLocked}
                onChange={e => { setForm(f => ({ ...f, invoiceDate: e.target.value })); setErrors(v => ({ ...v, invoiceDate: "" })); }}
              />
              {errors.invoiceDate && <p className="rv-field-err">{errors.invoiceDate}</p>}
            </div>
          </div>

          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Customer Name <span className="rv-required">*</span></label>
              <input
                className={`rv-input${errors.customerName ? " rv-input--err" : ""}`}
                placeholder="Full name"
                value={form.customerName}
                disabled={isLocked}
                onChange={e => { setForm(f => ({ ...f, customerName: e.target.value })); setErrors(v => ({ ...v, customerName: "" })); }}
              />
              {errors.customerName && <p className="rv-field-err">{errors.customerName}</p>}
            </div>
            <div className="rv-field">
              <label className="rv-label">Customer Phone</label>
              <input
                className="rv-input"
                placeholder="e.g. 9876543210"
                value={form.customerPhone}
                disabled={isLocked}
                onChange={e => setForm(f => ({ ...f, customerPhone: e.target.value }))}
              />
            </div>
          </div>

          <div className="rv-field">
            <label className="rv-label">Customer GSTIN</label>
            <input
              className="rv-input"
              placeholder="Optional — for business customers"
              value={form.customerGSTIN}
              disabled={isLocked}
              onChange={e => setForm(f => ({ ...f, customerGSTIN: e.target.value }))}
            />
          </div>

          {/* ── Quick Barcode Gun Scanner Bar ── */}
          {!isLocked && (
            <div className="rv-inv-scanner-card" style={{
              background: "linear-gradient(135deg, rgba(179,135,67,.06) 0%, rgba(185,167,146,.08) 100%)",
              border: "1px solid rgba(179,135,67,.25)",
              borderRadius: 12,
              padding: "10px 14px",
              marginBottom: 16,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <BarcodeIcon />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--dark, #2c2724)", letterSpacing: ".2px", whiteSpace: "nowrap" }}>
                    Scan Barcode Tag
                  </span>
                </div>
                <span style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#2a8a96",
                  background: "rgba(42,138,150,.12)",
                  padding: "2px 8px",
                  borderRadius: 12,
                  flexShrink: 0,
                  whiteSpace: "nowrap"
                }}>
                  Active
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, position: "relative" }}>
                  <input
                    ref={barcodeInputRef}
                    className="rv-input"
                    style={{ height: 36, fontSize: 13, paddingRight: scanLoading ? 36 : 12 }}
                    placeholder="Pull barcode gun trigger or type Tag # & press Enter…"
                    value={scanCode}
                    onChange={e => setScanCode(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleBarcodeScan(scanCode);
                      }
                    }}
                  />
                  {scanLoading && (
                    <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
                      <span className="rv-spinner" style={{ width: 14, height: 14 }} />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="rv-btn rv-btn--primary"
                  style={{
                    height: 36,
                    padding: "0 14px",
                    fontSize: 12.5,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    flexShrink: 0,
                    background: "linear-gradient(135deg, #b38743 0%, #8c6527 100%)",
                    border: "none",
                    color: "#fff"
                  }}
                  onClick={() => handleBarcodeScan(scanCode)}
                  disabled={scanLoading || !scanCode.trim()}
                >
                  <PlusIcon /> Add Tag
                </button>
              </div>

              {scanStatus && (
                <div style={{
                  fontSize: 11.5,
                  padding: "6px 10px",
                  borderRadius: 6,
                  marginTop: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: scanStatus.type === "success"
                    ? "rgba(109,138,109,.15)"
                    : scanStatus.type === "warn"
                      ? "rgba(230,126,34,.15)"
                      : "rgba(192,57,43,.15)",
                  color: scanStatus.type === "success"
                    ? "#2d6a4f"
                    : scanStatus.type === "warn"
                      ? "#d35400"
                      : "#c0392b",
                  fontWeight: 600,
                }}>
                  <span>{scanStatus.message}</span>
                  <button
                    type="button"
                    onClick={() => setScanStatus(null)}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: "inherit", padding: 0, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Line items ── */}
          <div className="rv-field">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <label className="rv-label" style={{ marginBottom: 0 }}>
                Line Items <span className="rv-required">*</span>
              </label>
              {!isLocked && (
                <button className="rv-btn rv-btn--ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={addLine}>
                  <PlusIcon /> Add Line
                </button>
              )}
            </div>

            {errors.lines && <p className="rv-field-err" style={{ marginBottom: 8 }}>{errors.lines}</p>}

            {lines.map((line, idx) => {
              const priceHint = getPriceHint(line.productID);
              const taxHint = getTaxHint(line.productID);
              const product = line.productID ? productMap.get(Number(line.productID)) : null;

              return (
                <div key={idx} style={{ border: "1px solid rgba(185,167,146,.2)", borderRadius: 10, padding: "12px 14px", marginBottom: 10, background: "rgba(185,167,146,.04)" }}>

                  {/* Line header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--taupe)", textTransform: "uppercase", letterSpacing: ".5px" }}>
                        Line {idx + 1}
                      </span>

                      {/* Product code badge */}
                      {product?.productCode && (
                        <span style={{ fontSize: 10, fontWeight: 600, color: "var(--taupe)", background: "rgba(185,167,146,.15)", padding: "2px 8px", borderRadius: 20, letterSpacing: ".3px" }}>
                          {product.productCode}
                        </span>
                      )}

                      {/* Category badge — new */}
                      {product?.categoryID && (() => {
                        const cat = categoryMap.get(Number(product.categoryID));
                        return cat ? (
                          <span style={{ fontSize: 10, fontWeight: 600, color: "#6d8a6d", background: "rgba(109,138,109,.1)", padding: "2px 8px", borderRadius: 20, letterSpacing: ".3px" }}>
                            {cat.categoryName}
                          </span>
                        ) : null;
                      })()}
                    </div>

                    {!isLocked && lines.length > 1 && (
                      <button
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", padding: 4, display: "flex", alignItems: "center" }}
                        onClick={() => removeLine(idx)}
                      ><TrashIcon /></button>
                    )}
                  </div>

                  {/* Product + Inventory Item */}
                  <div className="rv-field-row-2col" style={{ marginBottom: 4 }}>
                    <div className="rv-field" style={{ marginBottom: 0 }}>
                      <label className="rv-label">Product <span className="rv-required">*</span></label>
                      <SearchableDropdown
                        options={products}
                        valueKey="productID"
                        labelKey="productName"
                        value={line.productID}
                        disabled={isLocked}
                        onChange={val => handleProductChange(idx, val)}
                        placeholder="— Select product —"
                        searchPlaceholder="Search products…"
                        error={lineErrors[idx]?.productID}
                        pageSize={10}
                      />
                    </div>
                    <div className="rv-field" style={{ marginBottom: 0 }}>
                      <label className="rv-label">Inventory Item ID <span style={{ color: "var(--taupe)", fontWeight: 400 }}>(optional)</span></label>
                      <input
                        className="rv-input"
                        type="number" min="0" placeholder="0 for bulk"
                        value={line.inventoryItemID}
                        disabled={isLocked}
                        onChange={e => handleInventoryItemChange(idx, e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Inventory Item Info Line */}
                  {itemMeta[idx]?.loading && (
                    <p style={{ fontSize: 11, margin: "2px 0 6px", color: "var(--taupe)" }}>
                      ⏳ Fetching inventory item #{line.inventoryItemID} details…
                    </p>
                  )}

                  {itemMeta[idx]?.error && (
                    <p style={{ fontSize: 11, margin: "2px 0 6px", color: "#c0392b" }}>
                      ⚠ {itemMeta[idx].error}
                    </p>
                  )}

                  {itemMeta[idx] && !itemMeta[idx].loading && !itemMeta[idx].error && (
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      margin: "4px 0 8px",
                      padding: "4px 10px",
                      borderRadius: 6,
                      background: "rgba(185,167,146,.12)",
                      fontSize: 11,
                      color: "#6a5a52",
                      flexWrap: "wrap",
                    }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ fontSize: 12 }}>⚖</span>
                        <span>
                          Gross: <strong>{parseFloat(itemMeta[idx].grossWeight || 0).toFixed(3)}g</strong>
                          {" · "}
                          Less: <strong>{parseFloat(itemMeta[idx].lessWeight || 0).toFixed(3)}g</strong>
                          {" · "}
                          Net: <strong style={{ color: "var(--dark, #2c2724)" }}>{parseFloat(itemMeta[idx].netWeight || 0).toFixed(3)}g</strong>
                        </span>
                      </span>
                      <span style={{ fontSize: 10, color: "#4d7a4d", fontWeight: 600, background: "rgba(109,138,109,.15)", padding: "1px 6px", borderRadius: 10 }}>
                        ✓ Billed Wt Auto-filled
                      </span>
                    </div>
                  )}

                  {/* Price hint */}
                  {priceHint && (
                    <p style={{ fontSize: 11, marginBottom: 8, marginTop: 2, padding: "4px 8px", borderRadius: 6, background: priceHint.warn ? "rgba(192,57,43,.07)" : "rgba(185,167,146,.1)", color: priceHint.warn ? "#c0392b" : "var(--taupe)" }}>
                      {priceHint.warn ? "⚠ " : "ℹ "}{priceHint.text}
                    </p>
                  )}

                  {/* Qty + Billed Weight + Unit Price */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                    <div className="rv-field" style={{ marginBottom: 0 }}>
                      <label className="rv-label">Quantity <span className="rv-required">*</span></label>
                      <input
                        className={`rv-input${lineErrors[idx]?.quantity ? " rv-input--err" : ""}`}
                        type="number" min="0" step="1" placeholder="0"
                        value={line.quantity}
                        disabled={isLocked}
                        onChange={e => updateLine(idx, "quantity", e.target.value)}
                      />
                      {!line.quantity && line.unitPrice && (
                        <p style={{ fontSize: 10.5, marginTop: 3, color: "var(--taupe)" }}>
                          Enter quantity to calculate
                        </p>
                      )}
                      {lineErrors[idx]?.quantity && <p className="rv-field-err">{lineErrors[idx].quantity}</p>}
                    </div>
                    <div className="rv-field" style={{ marginBottom: 0 }}>
                      <label className="rv-label">Billed Wt (g)</label>
                      <input
                        className="rv-input"
                        type="number" min="0" step="0.001" placeholder="0.000"
                        value={line.billedWeight}
                        disabled={isLocked}
                        onChange={e => updateLine(idx, "billedWeight", e.target.value)}
                      />
                    </div>
                    <div className="rv-field" style={{ marginBottom: 0 }}>
                      <label className="rv-label">Unit Price (₹) <span className="rv-required">*</span></label>
                      <input
                        className={`rv-input${lineErrors[idx]?.unitPrice ? " rv-input--err" : ""}`}
                        type="number" min="0" step="0.01" placeholder="0.00"
                        value={line.unitPrice}
                        disabled={isLocked}
                        onChange={e => updateLine(idx, "unitPrice", e.target.value)}
                        style={line.unitPrice ? { fontWeight: 600 } : {}}
                      />
                      {lineErrors[idx]?.unitPrice && <p className="rv-field-err">{lineErrors[idx].unitPrice}</p>}
                    </div>
                  </div>

                  {/* Tax % + tax hint + Calculated summary */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8, marginBottom: 8 }}>
                    <div className="rv-field" style={{ marginBottom: 0 }}>
                      <label className="rv-label">Tax %</label>
                      <input
                        className="rv-input"
                        type="number" min="0" max="100" step="0.01" placeholder="0"
                        value={line.taxPercentage}
                        disabled={isLocked}
                        onChange={e => updateLine(idx, "taxPercentage", e.target.value)}
                      />
                      {/* Tax config hint */}
                      {taxHint && (
                        <p style={{ fontSize: 10.5, marginTop: 3, color: taxHint.warn ? "#c0392b" : "var(--taupe)" }}>
                          {taxHint.warn ? "⚠ " : ""}{taxHint.text}
                        </p>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, alignItems: "end" }}>
                      {[
                        ["Line Amt", line.lineAmount],
                        ["Tax Amt", line.taxAmount],
                        ["Total", line.totalPrice],
                      ].map(([lbl, val]) => (
                        <div key={lbl} style={{ textAlign: "center", background: "rgba(185,167,146,.08)", borderRadius: 8, padding: "6px 4px" }}>
                          <p style={{ fontSize: 10, color: "var(--taupe)", marginBottom: 2 }}>{lbl}</p>
                          <p style={{ fontSize: 13, fontWeight: 600, color: "var(--dark)" }}>{fmtAmt(val)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              );
            })}
          </div>

          {/* ── Grand total bar ── */}
          {lines.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, padding: "12px 14px", background: "rgba(185,167,146,.1)", borderRadius: 10, marginTop: 4 }}>
              {[
                ["Subtotal", totalAmount],
                ["Total Tax", totalTaxAmount],
                ["Grand Total", grossAmount],
              ].map(([lbl, val]) => (
                <div key={lbl} style={{ textAlign: "center" }}>
                  <p style={{ fontSize: 11, color: "var(--taupe)", marginBottom: 3 }}>{lbl}</p>
                  <p style={{ fontSize: 15, fontWeight: 700, color: "var(--dark)" }}>{fmtAmt(val)}</p>
                </div>
              ))}
            </div>
          )}

        </div>

        {/* ── Footer buttons ── */}
        <div className="rv-modal-footer" style={{ flexShrink: 0 }}>
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>
            {isLocked ? "Close" : "Cancel"}
          </button>
          {!isLocked && (
            <>
              <button
                className="rv-btn rv-btn--ghost"
                style={{ borderColor: "var(--taupe)" }}
                onClick={() => handleSubmit("Draft")}
                disabled={saving}
              >
                {saving && <span className="rv-spinner" />}
                Save as Draft
              </button>
              <button
                className="rv-btn rv-btn--primary"
                onClick={() => handleSubmit("Finalised")}
                disabled={saving}
              >
                {saving && <span className="rv-spinner" />}
                Finalise Invoice
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Invoice Detail Drawer ── */
function InvoiceDrawer({ invoice, items, products, inventoryItems = [], onClose, onEdit, onCancel, onPrint }) {
  const lines = items.filter(i => i.invoiceID === invoice.invoiceID);
  const statusConf = STATUS_CONFIG[invoice.invoiceStatus] ?? STATUS_CONFIG.Draft;

  const inventoryItemMap = useMemo(() =>
    new Map((inventoryItems || []).map(i => [Number(i.inventoryItemID), i])),
    [inventoryItems]);

  const fmt = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtAmt = v => `₹${parseFloat(v || 0).toFixed(2)}`;

  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-invitem-drawer" style={{ maxWidth: 680, width: "100%", maxHeight: "90vh" }} onClick={e => e.stopPropagation()}>
        <div className="rv-invitem-drawer-header" style={{ paddingRight: 48 }}>
          <div className="rv-invitem-drawer-avatar"><InvoiceIcon /></div>
          <div className="rv-invitem-drawer-title-wrap">
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
              <div className="rv-invitem-drawer-name" style={{ marginBottom: 0 }}>{invoice.invoiceNumber}</div>
              <span className={`rv-inv-status-badge rv-inv-status--${statusConf.color}`}>
                {invoice.invoiceStatus}
              </span>
            </div>
            <span className="rv-invitem-id-chip">{invoice.customerName} · {fmt(invoice.invoiceDate)}</span>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-invitem-drawer-body">

          {/* Customer info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
            {[
              ["Customer", invoice.customerName || "—"],
              ["Phone", invoice.customerPhone || "—"],
              ["GSTIN", invoice.customerGSTIN || "—"],
              ["Date", fmt(invoice.invoiceDate)],
            ].map(([lbl, val]) => (
              <div key={lbl} style={{ padding: "8px 10px", background: "rgba(185,167,146,.06)", borderRadius: 8 }}>
                <p style={{ fontSize: 10, color: "var(--taupe)", marginBottom: 2 }}>{lbl}</p>
                <p style={{ fontSize: 13, fontWeight: 600, color: "var(--dark)" }}>{val}</p>
              </div>
            ))}
          </div>

          {/* Line items */}
          {lines.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--taupe)", textAlign: "center", padding: "20px 0" }}>No line items.</p>
          ) : (
            <div className="rv-table-wrap">
              <table className="rv-table" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Wt (g)</th>
                    <th>Unit ₹</th>
                    <th>Tax%</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(l => {
                    const prod = products.find(p => p.productID === l.productID);
                    const invItem = l.inventoryItemID ? inventoryItemMap.get(Number(l.inventoryItemID)) : null;
                    return (
                      <tr key={l.invoiceItemID}>
                        <td>
                          <div>{prod?.productName ?? `#${l.productID}`}</div>
                          {l.inventoryItemID && (
                            <div style={{ fontSize: 10, color: "var(--taupe)", marginTop: 2 }}>
                              Item #{l.inventoryItemID}
                              {invItem && ` (Net: ${parseFloat(invItem.netWeight || 0).toFixed(3)}g)`}
                            </div>
                          )}
                        </td>
                        <td>{l.quantity}</td>
                        <td>{l.billedWeight ? `${parseFloat(l.billedWeight).toFixed(3)}g` : "—"}</td>
                        <td>{fmtAmt(l.unitPrice)}</td>
                        <td>{l.taxPercentage}%</td>
                        <td><strong>{fmtAmt(l.totalPrice)}</strong></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Totals */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 12 }}>
            {[
              ["Subtotal", invoice.totalAmount],
              ["Tax", invoice.totalTaxAmount],
              ["Grand Total", invoice.grossAmount],
            ].map(([lbl, val]) => (
              <div key={lbl} style={{ textAlign: "center", padding: "8px", background: "rgba(185,167,146,.08)", borderRadius: 8 }}>
                <p style={{ fontSize: 10, color: "var(--taupe)", marginBottom: 2 }}>{lbl}</p>
                <p style={{ fontSize: 14, fontWeight: 700 }}>{fmtAmt(val)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rv-invitem-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
          <button
            className="rv-btn"
            style={{
              background: "linear-gradient(135deg, #b38743 0%, #8c6527 100%)",
              color: "#fff",
              border: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontWeight: 600,
              padding: "8px 14px",
              borderRadius: "8px",
              cursor: "pointer",
            }}
            onClick={() => onPrint && onPrint(invoice)}
          >
            <PrinterIcon /> Print A4 Invoice
          </button>
          {invoice.invoiceStatus === "Draft" && (
            <>
              <button className="rv-btn rv-btn--danger" onClick={() => onCancel(invoice)}>
                Cancel Invoice
              </button>
              <button className="rv-btn rv-btn--primary" onClick={() => { onClose(); onEdit(invoice); }}>
                <EditIcon /> Edit
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN INVOICE PAGE
══════════════════════════════════════ */
export default function InvoicePage() {
  const [invoices, setInvoices] = useState([]);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [printInvoice, setPrintInvoice] = useState(null);
  const [toast, setToast] = useState(null);


  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 160, 120, 180, 130, 110, 130, 100]);


  const [prices, setPrices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [taxConfigs, setTaxConfigs] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);

  const fetchAll = async () => {
    try {
      setLoading(true); setTableReady(false);
      const [invData, itemData, prodData, priceData, catData, taxData, invItemData] = await Promise.all([
        invoiceApi.getAll(),
        invoiceItemDtlApi.getAll(),
        productApi.getAll(),
        productPriceApi.getAll(),
        productCategoryApi.getAll(),
        taxConfigApi.getAll(),
        inventoryItemDtlApi.getAll().catch(() => []),
      ]);
      setInvoices(Array.isArray(invData) ? invData : invData?.data ?? []);
      setItems(Array.isArray(itemData) ? itemData : itemData?.data ?? []);
      setProducts(Array.isArray(prodData) ? prodData : prodData?.data ?? []);
      setPrices(Array.isArray(priceData) ? priceData : priceData?.data ?? []);
      setCategories(Array.isArray(catData) ? catData : catData?.data ?? []);
      setTaxConfigs(Array.isArray(taxData) ? taxData : taxData?.data ?? []);
      setInventoryItems(Array.isArray(invItemData) ? invItemData : invItemData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load invoices.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Auto-generate invoice number ── */
  const generateInvoiceNumber = useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");

    const prefix = `INV-${year}-${month}-`;
    const existing = invoices
      .map(inv => inv.invoiceNumber ?? "")
      .filter(n => n.startsWith(prefix))
      .map(n => parseInt(n.replace(prefix, ""), 10))
      .filter(n => !isNaN(n));

    const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    return `${prefix}${String(next).padStart(3, "0")}`;
  }, [invoices]);

  /* ── Open new invoice with pre-scanned item (from BarcodeScanPopup) ── */
  const openWithScannedItem = useCallback((item) => {
    if (!item) return;
    const invNum = generateInvoiceNumber();
    const billedWeight = item.netWeight != null
      ? String(item.netWeight)
      : item.grossWeight != null ? String(item.grossWeight) : "";

    const initialLine = {
      invoiceItemID: 0,
      productID: item.productID ? String(item.productID) : "",
      inventoryItemID: String(item.inventoryItemID),
      quantity: 1,
      billedWeight,
      unitPrice: "",
      taxConfigID: 0,
      taxPercentage: "",
      lineAmount: 0,
      taxAmount: 0,
      totalPrice: 0,
    };

    setModal({
      data: {
        invoiceNumber: invNum,
        lines: [initialLine],
      },
    });
    showToast(`Started new invoice with scanned item #${item.inventoryItemID}`);
  }, [generateInvoiceNumber]);

  useEffect(() => {
    const stored = sessionStorage.getItem("reva_scanned_item_for_invoice");
    if (stored) {
      try {
        const item = JSON.parse(stored);
        sessionStorage.removeItem("reva_scanned_item_for_invoice");
        if (item?.inventoryItemID) {
          openWithScannedItem(item);
        }
      } catch (_) {}
    }

    const onScanInvoiceEvent = (e) => {
      if (e?.detail) openWithScannedItem(e.detail);
    };
    window.addEventListener("reva:open_invoice_with_item", onScanInvoiceEvent);
    return () => window.removeEventListener("reva:open_invoice_with_item", onScanInvoiceEvent);
  }, [openWithScannedItem]);

  /* ── Save flow ── */
  const handleSave = async ({ header, lines }) => {
    setSaving(true);
    try {
      const now = new Date().toISOString();

      if (header.invoiceID) {
        // ── EDIT ──
        // 1. Update header
        await invoiceApi.update({
          invoiceID: header.invoiceID,
          invoiceNumber: header.invoiceNumber,
          invoiceDate: header.invoiceDate,
          customerName: header.customerName,
          customerPhone: header.customerPhone,
          customerGSTIN: header.customerGSTIN,
          totalAmount: header.totalAmount,
          totalTaxAmount: header.totalTaxAmount,
          grossAmount: header.grossAmount,
          invoiceStatus: header.invoiceStatus,
          lastUPDTTM: now,
          lastUPDID: 0,
        });

        // 2. Fetch existing lines fresh to know which invoiceItemIDs already exist
        const existingItems = items.filter(i => i.invoiceID === header.invoiceID);
        const existingIDs = new Set(existingItems.map(i => i.invoiceItemID));

        const toUpdate = lines.filter(l => l.invoiceItemID && existingIDs.has(l.invoiceItemID));
        const toCreate = lines.filter(l => !l.invoiceItemID || !existingIDs.has(l.invoiceItemID));

        await Promise.all([
          ...toUpdate.map(l =>
            invoiceItemDtlApi.update({
              invoiceItemID: l.invoiceItemID,
              invoiceID: header.invoiceID,
              productID: l.productID,
              inventoryItemID: l.inventoryItemID ? Number(l.inventoryItemID) : null,
              quantity: l.quantity,
              billedWeight: l.billedWeight,
              unitPrice: l.unitPrice,
              lineAmount: l.lineAmount,
              taxConfigID: l.taxConfigID,
              taxPercentage: l.taxPercentage,
              taxAmount: l.taxAmount,
              totalPrice: l.totalPrice,
            })
          ),
          ...toCreate.map(l =>
            invoiceItemDtlApi.create({
              invoiceItemID: 0,
              invoiceID: header.invoiceID,
              productID: l.productID,
              inventoryItemID: l.inventoryItemID ? Number(l.inventoryItemID) : null,
              quantity: l.quantity,
              billedWeight: l.billedWeight,
              unitPrice: l.unitPrice,
              lineAmount: l.lineAmount,
              taxConfigID: l.taxConfigID,
              taxPercentage: l.taxPercentage,
              taxAmount: l.taxAmount,
              totalPrice: l.totalPrice,
            })
          ),
        ]);

        showToast("Invoice updated.");

      } else {
        // ── CREATE ──
        // 1. POST header → get invoiceID
        const created = await invoiceApi.create({
          invoiceID: 0,
          invoiceNumber: header.invoiceNumber,
          invoiceDate: header.invoiceDate,
          customerName: header.customerName,
          customerPhone: header.customerPhone,
          customerGSTIN: header.customerGSTIN,
          totalAmount: header.totalAmount,
          totalTaxAmount: header.totalTaxAmount,
          grossAmount: header.grossAmount,
          invoiceStatus: header.invoiceStatus,
          lastUPDTTM: now,
          lastUPDID: 0,
        });
        const invoiceID = created?.invoiceID ?? created?.data?.invoiceID;
        if (!invoiceID) throw new Error("No invoiceID returned from server.");

        // 2. POST each line
        await Promise.all(lines.map(l =>
          invoiceItemDtlApi.create({
            invoiceItemID: 0,
            invoiceID,
            productID: l.productID,
            inventoryItemID: l.inventoryItemID,
            quantity: l.quantity,
            billedWeight: l.billedWeight,
            unitPrice: l.unitPrice,
            lineAmount: l.lineAmount,
            taxConfigID: l.taxConfigID,
            taxPercentage: l.taxPercentage,
            taxAmount: l.taxAmount,
            totalPrice: l.totalPrice,
          })
        ));

        showToast(
          header.invoiceStatus === "Finalised"
            ? "Invoice finalised successfully."
            : "Invoice saved as draft."
        );
      }

      setModal(null);
      emitDataMutation("invoice");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Cancel invoice ── */
  const handleCancelInvoice = (invoice) => {
    setConfirm({
      type: "cancel",
      invoice,
      title: "Cancel Invoice?",
      message: `Invoice ${invoice.invoiceNumber} will be marked as Cancelled. Stock will NOT be affected. This cannot be undone.`,
      confirmLabel: "Yes, Cancel Invoice",
      confirmClass: "rv-btn--danger",
    });
  };

  /* ── Delete invoice (Draft only) ── */
  const handleDeleteInvoice = (invoice) => {
    setConfirm({
      type: "delete",
      invoice,
      title: "Delete Invoice?",
      message: `Invoice ${invoice.invoiceNumber} will be permanently deleted. This action cannot be undone.`,
      confirmLabel: "Delete",
      confirmClass: "rv-btn--danger",
    });
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    try {
      const now = new Date().toISOString();
      if (confirm.type === "delete") {
        await invoiceApi.remove(confirm.invoice.invoiceID);
        showToast("Invoice deleted.");
      } else if (confirm.type === "cancel") {
        await invoiceApi.update({
          invoiceID: confirm.invoice.invoiceID,
          invoiceNumber: confirm.invoice.invoiceNumber,
          invoiceDate: confirm.invoice.invoiceDate,
          customerName: confirm.invoice.customerName,
          customerPhone: confirm.invoice.customerPhone,
          customerGSTIN: confirm.invoice.customerGSTIN,
          totalAmount: confirm.invoice.totalAmount,
          totalTaxAmount: confirm.invoice.totalTaxAmount,
          grossAmount: confirm.invoice.grossAmount,
          invoiceStatus: "Cancelled",
          lastUPDTTM: now,
          lastUPDID: 0,
        });
        showToast("Invoice cancelled.");
      }
      setConfirm(null);
      setDrawer(null);
      emitDataMutation("invoice");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Action failed.", "error");
    }
  };

  /* ── Filtered list ── */
  const filtered = invoices.filter(inv => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      inv.invoiceNumber?.toLowerCase().includes(q) ||
      inv.customerName?.toLowerCase().includes(q) ||
      inv.customerPhone?.toLowerCase().includes(q) ||
      String(inv.invoiceID).includes(q);
    const matchStatus = filterStatus === "all" || inv.invoiceStatus === filterStatus;
    return matchSearch && matchStatus;
  });

  const { paged, page, totalPages, pageSize, setPage, setPageSize, pageStart, pageEnd, totalItems } =
    usePagination(filtered, 10);

  const fmt = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtAmt = v => `₹${parseFloat(v || 0).toFixed(2)}`;

  /* ── Open edit modal with existing line items ── */
  const openEdit = (invoice) => {
    const invoiceLines = items.filter(i => i.invoiceID === invoice.invoiceID);
    setModal({ data: { ...invoice, lines: invoiceLines } });
  };


  return (
    <div className="rv-inv-page">

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {modal && (
        <InvoiceModal
          initial={modal.data}
          products={products}
          allItems={items}
          inventoryItems={inventoryItems}
          prices={prices}
          categories={categories}
          taxConfigs={taxConfigs}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}

      {drawer && (
        <InvoiceDrawer
          invoice={drawer}
          items={items}
          products={products}
          inventoryItems={inventoryItems}
          onClose={() => setDrawer(null)}
          onEdit={openEdit}
          onCancel={handleCancelInvoice}
          onPrint={(inv) => setPrintInvoice(inv)}
        />
      )}

      {printInvoice && (
        <PrintInvoiceModal
          invoice={printInvoice}
          lines={items.filter((i) => i.invoiceID === printInvoice.invoiceID)}
          products={products}
          categories={categories}
          inventoryItems={inventoryItems}
          onClose={() => setPrintInvoice(null)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          confirmClass={confirm.confirmClass}
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* ── Topbar ── */}
      <div className="rv-inv-topbar">
        <div className="rv-inv-topbar-left">
          <h2 className="rv-inv-heading">Invoices</h2>
          <span className="rv-inv-count">{invoices.length} total</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: { invoiceNumber: generateInvoiceNumber() } })}>
          <PlusIcon /> New Invoice
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="rv-inv-filters">
        <div className="rv-inv-search">
          <SearchIcon />
          <input
            placeholder="Search by invoice number, customer name or phone…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-inv-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>
        <div className="rv-filter-tabs">
          {["all", "Draft", "Finalised", "Cancelled"].map(s => (
            <button
              key={s}
              className={`rv-filter-tab${filterStatus === s ? " rv-filter-tab--active" : ""}`}
              onClick={() => { setFilterStatus(s); setPage(1); }}
            >
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="rv-card rv-inv-card">
        {loading ? (
          <div className="rv-inv-empty"><div className="rv-loader" /><p>Loading invoices…</p></div>
        ) : filtered.length === 0 ? (
          <div className="rv-inv-empty">
            <div className="rv-empty-icon"><InvoiceIcon /></div>
            <p className="rv-empty-title">No invoices found</p>
            <p className="rv-empty-sub">
              {search ? "Try a different search." : "Create your first invoice to get started."}
            </p>
            {!search && (
              <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: { invoiceNumber: generateInvoiceNumber() } })}>
                <PlusIcon /> New Invoice
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="rv-desktop-table-view">
              <div className="rv-table-wrap rv-inv-table-wrap">
                <table className="rv-table rv-inv-table" ref={tableRef}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Invoice No.</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((inv, i) => {
                      const statusConf = STATUS_CONFIG[inv.invoiceStatus] ?? STATUS_CONFIG.Draft;
                      const isDraft = inv.invoiceStatus === "Draft";
                      return (
                        <tr
                          key={inv.invoiceID}
                          className="rv-txn-row"
                          style={{ cursor: "pointer" }}
                          onClick={() => setDrawer(inv)}
                        >
                          <td>{pageStart + i}</td>
                          <td><span className="rv-txn-id-badge">{inv.invoiceNumber}</span></td>
                          <td>{fmt(inv.invoiceDate)}</td>
                          <td>{inv.customerName}</td>
                          <td>{inv.customerPhone || "—"}</td>
                          <td>
                            <span className={`rv-inv-status-badge rv-inv-status--${statusConf.color}`}>
                              {inv.invoiceStatus}
                            </span>
                          </td>
                          <td><strong>{fmtAmt(inv.grossAmount)}</strong></td>
                          <td onClick={e => e.stopPropagation()}>
                            <div className="rv-actions">
                              <button
                                className="rv-action-btn"
                                title="Print A4 Invoice"
                                onClick={() => setPrintInvoice(inv)}
                              ><PrinterIcon /></button>
                              {isDraft && (
                                <button
                                  className="rv-action-btn rv-action-btn--edit"
                                  title="Edit"
                                  onClick={() => openEdit(inv)}
                                ><EditIcon /></button>
                              )}
                              {isDraft && (
                                <button
                                  className="rv-action-btn rv-action-btn--del"
                                  title="Delete"
                                  onClick={() => handleDeleteInvoice(inv)}
                                ><TrashIcon /></button>
                              )}
                              {!isDraft && (
                                <button
                                  className="rv-action-btn rv-action-btn--edit"
                                  title="View"
                                  onClick={() => setDrawer(inv)}
                                ><EyeIcon /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards View */}
            <div className="rv-mobile-cards-view">
              <RvDataCardList
                loading={loading}
                items={paged}
                emptyIcon={<InvoiceIcon />}
                emptyTitle="No invoices found"
                emptySubtitle={search ? "Try a different search term." : "Create your first customer invoice to get started."}
                emptyAction={
                  !search && (
                    <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: { invoiceNumber: generateInvoiceNumber() } })}>
                      <PlusIcon /> New Invoice
                    </button>
                  )
                }
                renderCard={(inv, i) => {
                  const statusConf = STATUS_CONFIG[inv.invoiceStatus] ?? STATUS_CONFIG.Draft;
                  const isDraft = inv.invoiceStatus === "Draft";
                  return (
                    <RvDataCard
                      key={inv.invoiceID}
                      index={pageStart + i}
                      title={inv.customerName || "Customer"}
                      subtitle={inv.invoiceNumber}
                      badge={
                        <span className={`rv-inv-status-badge rv-inv-status--${statusConf.color}`}>
                          {inv.invoiceStatus}
                        </span>
                      }
                      fields={[
                        { label: "Invoice Date", value: fmt(inv.invoiceDate) },
                        { label: "Gross Amount", value: <strong style={{ color: "var(--dark)", fontSize: 13.5 }}>{fmtAmt(inv.grossAmount)}</strong> },
                        { label: "Customer Phone", value: inv.customerPhone || "—" },
                        { label: "Items Count", value: inv.itemCount ? `${inv.itemCount} items` : "—" },
                      ]}
                      onClick={() => setDrawer(inv)}
                      actions={
                        <div className="rv-actions" onClick={e => e.stopPropagation()}>
                          <button
                            className="rv-action-btn"
                            title="Print A4 Invoice"
                            onClick={() => setPrintInvoice(inv)}
                          ><PrinterIcon /></button>
                          {isDraft && (
                            <button
                              className="rv-action-btn rv-action-btn--edit"
                              title="Edit"
                              onClick={() => openEdit(inv)}
                            ><EditIcon /></button>
                          )}
                          {isDraft && (
                            <button
                              className="rv-action-btn rv-action-btn--del"
                              title="Delete"
                              onClick={() => handleDeleteInvoice(inv)}
                            ><TrashIcon /></button>
                          )}
                          {!isDraft && (
                            <button
                              className="rv-action-btn rv-action-btn--edit"
                              title="View"
                              onClick={() => setDrawer(inv)}
                            ><EyeIcon /></button>
                          )}
                        </div>
                      }
                    />
                  );
                }}
              />
            </div>

            {/* Shared Pagination */}
            <Pagination
              page={page} totalPages={totalPages} pageSize={pageSize}
              setPage={setPage} setPageSize={setPageSize}
              pageStart={pageStart} pageEnd={pageEnd} totalItems={totalItems}
            />
          </>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint rv-desktop-table-view"><GripIcon /> Drag column borders to resize · Click a row to view details</p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function EditIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>; }
function TrashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>; }
function GripIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" /></svg>; }
function InvoiceIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>; }
function EyeIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>; }
function PrinterIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="16" height="16"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>; }
function BarcodeIcon() { return <i className="bi bi-upc-scan" style={{ fontSize: 15, lineHeight: 1 }} />; }